import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { selectStyles } from "../../types/SelectStyles";
import CreatableSelect from "react-select/creatable";
import { INewRole } from "../AddUserModal/interfaces/role.interface";
import { FaTimes } from "react-icons/fa";

interface IAddSuperuserModalProps {
  setShowAddSuperuserModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddSuperuser: (newSuperuser: INewRole) => void;
  roleOptions: { value: string; label: string };
  darkMode: boolean;
  userList: { value: string; label: string }[];
}

const AddSuperuserModal: React.FC<IAddSuperuserModalProps> = ({
  setShowAddSuperuserModal,
  handleAddSuperuser,
  roleOptions,
  darkMode,
  userList,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState(roleOptions);
  const [extractedUser, setExtractedUser] = useState<INewRole | null>(null);

  const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email) || !extractedUser) return;
    handleAddSuperuser(extractedUser);

    setShowAddSuperuserModal(false);
  };

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  return (
    <div className="modal-overlay">
      <div className="modal-panel p-4 w-[500px] relative">
        <h2 className="text-xl title-base-color text-center mb-1">
          {t("adminPanel.addUser")}
        </h2>
        <button
          aria-label="Close"
          className="absolute right-2 top-3 z-10 modal-close-btn"
          onClick={() => setShowAddSuperuserModal(false)}
          type="button"
        >
          <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
        </button>
        <form onSubmit={handleAdd}>
          <div className="mb-4 text-sm">
            <label className="block title-base-color mb-1 text-base">
              {t("adminPanel.columns.email")}
              <span className="text-del-red dark:text-red-400">*</span>
            </label>

            <CreatableSelect<{ value: string; label: string }>
              value={email ? { value: email, label: email } : null}
              onChange={(opt) => {
                const val = opt?.value ?? "";
                setEmail(val);

                setExtractedUser({
                  name: opt?.label ?? null,
                  email: val,
                  right_id: selectedRole.value,
                });
              }}
              options={userList}
              isClearable
              placeholder={t("adminPanel.emailPlaceholder")}
              styles={{
                input: (base) => ({
                  ...base,
                  color: darkMode ? "#FFFFFF" : "#000000",
                }),
                option: (base, { isFocused }) => ({
                  ...base,
                  backgroundColor: isFocused
                    ? darkMode
                      ? "#86BC25"
                      : "#86BC25"
                    : darkMode
                      ? "#374151"
                      : "#FFFFFF",
                  color: darkMode ? "#FFFFFF" : "#000000",
                }),
                ...selectStyles(darkMode),
              }}
              formatCreateLabel={(v) => `Create "${v}"`}
            />
          </div>

          <div className="mb-4 text-sm">
            <label className="block title-base-color mb-1 text-base">
              {t("adminPanel.columns.role")}
              <span className="text-del-red dark:text-red-400">*</span>
            </label>

            <Select
              value={roleOptions}
              isDisabled
              required
              classNamePrefix="react-select"
              styles={{
                ...selectStyles(darkMode),
                option: (base, { isFocused }) => ({
                  ...base,
                  backgroundColor: isFocused
                    ? darkMode
                      ? "#86BC25"
                      : "#86BC25"
                    : darkMode
                      ? "#374151"
                      : "#FFFFFF",
                  color: darkMode ? "#FFFFFF" : "#000000",
                }),
              }}
            />
          </div>

          <div className="flex justify-end space-x-4 mt-2 text-sm">
            <button
              type="button"
              onClick={() => setShowAddSuperuserModal(false)}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              {t("adminPanel.cancel")}
            </button>

            <button
              type="submit"
              className={`px-4 py-2 ${email ? "btn-green" : "btn-disabled"}`}
            >
              {t("adminPanel.add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { AddSuperuserModal };
